import { meStore } from '@gozoki/api'
import { Toaster } from '@gozoki/ui/components/ui/sonner'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { type CSSProperties, useLayoutEffect, useState } from 'react'
import { BottomBar } from '~/components/dashboard/BottomBar'
import { CaLiveHeroCard } from '~/components/dashboard/CaLiveHeroCard'
import { DashboardHeader } from '~/components/dashboard/DashboardHeader'
import { FluxSupplyCard } from '~/components/dashboard/FluxSupplyCard'
import { CamerasCard, CaTotalCard, SatisfactionClientCard } from '~/components/dashboard/LeftPanels'
import { PlanningWeekCard } from '~/components/dashboard/PlanningWeekCard'
import {
  ContactCard,
  ProcessCard,
  StockRestantCard,
  SuiviSavCard,
} from '~/components/dashboard/SidePanels'
import { setToken } from '~/utils/api'

// Le dashboard boutique est un écran kiosk 3840×2160 (iiyama TF4339AS, 16:9 exact). La maquette
// (Figma 10715:55849) fait 2044 × 1453, soit un ratio plus haut que le 16:9 de l'écran : on garde
// son rythme vertical (1453 px, cotes lues 1:1) et on porte la largeur au 16:9 exact. Les ~539 px
// gagnés vont à la colonne centrale, la seule dont les widgets sont fluides en largeur ; les
// colonnes latérales gardent les 289 px du Figma.
// Largeur non arrondie : les deux termes du min() (innerW/CANVAS_W, innerH/CANVAS_H) sont alors
// rigoureusement égaux sur un écran 16:9, donc zéro letterbox (l'arrondi laissait ~0,5 px).
const CANVAS_H = 1453
const CANVAS_W = (CANVAS_H * 16) / 9
const SIDE_COLUMN_WIDTH = 289
/** Toasts aux couleurs du dashboard (variables de Sonner), assez larges pour tenir sur une ligne. */
const TOASTER_STYLE = {
  '--width': '520px',
  '--normal-bg': '#F9F8F1',
  '--normal-text': '#072C1A',
  '--normal-border': '#0000000F',
  '--border-radius': '16px',
} as CSSProperties

const useCanvasScale = () => {
  const [scale, setScale] = useState(1)
  useLayoutEffect(() => {
    const update = () =>
      setScale(Math.min(window.innerWidth / CANVAS_W, window.innerHeight / CANVAS_H))
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])
  return scale
}

export function HomePage() {
  const navigate = useNavigate()
  const scale = useCanvasScale()
  const { data } = useQuery({
    queryKey: ['store', 'me'],
    queryFn: meStore,
  })

  const logout = async () => {
    await setToken(null)
    navigate({ to: '/', replace: true })
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-hidden bg-[#F9F8F1]">
      {/* shrink-0 : dans une fenêtre plus étroite que le canvas (iframe d'aperçu du BO), le flex
          parent le comprimait jusqu'à sa largeur min-content, que les widgets mesurés font varier
          au fil du chargement : le dashboard se resserrait vers le centre. Seul le scale réduit. */}
      <div
        className="flex shrink-0 flex-col gap-[8px] p-[8px] text-[#152013]"
        style={{
          width: CANVAS_W,
          height: CANVAS_H,
          transform: `scale(${scale})`,
          transformOrigin: 'center',
        }}
      >
        <DashboardHeader storeLabel={data?.store.label} onLogout={logout} />

        <main className="flex min-h-0 flex-1 gap-[8px]">
          <aside
            className="flex min-h-0 shrink-0 flex-col gap-[8px]"
            style={{ width: SIDE_COLUMN_WIDTH }}
          >
            <CaLiveHeroCard />
            <CaTotalCard />
            <CamerasCard />
            <SatisfactionClientCard />
          </aside>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-[8px]">
            <FluxSupplyCard />
            <PlanningWeekCard />
            <BottomBar
              storeLabel={data?.store.label}
              storeAddress={data?.store.address}
              storeDepartmentCode={data?.store.departmentCode}
            />
          </div>

          <aside
            className="flex min-h-0 shrink-0 flex-col gap-[8px]"
            style={{ width: SIDE_COLUMN_WIDTH }}
          >
            <StockRestantCard />
            <SuiviSavCard />
            <ProcessCard />
            <ContactCard storeLabel={data?.store.label} />
          </aside>
        </main>

        {/* Dans le canvas et non à la racine : un `position: fixed` sous le `scale()` se cale sur le
            canvas, le toast est donc à l'échelle du dashboard au lieu d'être minuscule sur l'écran
            4K (Sonner n'utilise pas de portail). En haut au centre, sur la partie vide du bandeau,
            pour ne masquer aucune carte ; taille et bouton à la mesure d'un écran tactile. */}
        <Toaster
          position="top-center"
          offset={16}
          style={TOASTER_STYLE}
          toastOptions={{
            classNames: {
              title: 'font-sans! text-[18px]! font-semibold!',
              actionButton:
                'h-[40px]! rounded-[12px]! bg-[#4F6A47]! px-[16px]! font-sans! text-[16px]! font-semibold!',
              closeButton: 'size-[28px]!',
            },
          }}
        />
      </div>
    </div>
  )
}
